/** Mass restore — the batch action on the Changes grid.
 *
 *  Restores the selected change rows' `state_before` onto the live objects,
 *  typically over an `all_matching` selection filtered by task_id ("roll back
 *  everything batch X did"). Same flow shape as batch edit: reason → dry run
 *  (background task, writes nothing, per-row report) → apply. There is no
 *  receipt here — the backend refuses to touch any row whose live values no
 *  longer match the change's own after-state (`stale`), which is stronger than
 *  a fingerprint. Re-running a partial rollback is safe (`no_change`).
 */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { runBatch } from '@/api/batch';
import type { BatchRestoreOutcome, BatchRestoreTaskResult } from '@/api/changes';
import { RESTORE_OUTCOME_LABELS, RESTORE_OUTCOME_SEVERITY } from '@/api/changes';
import * as bgtasksApi from '@/api/bgtasks';
import { TASK_STATE, isActiveState } from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { useSnackbar } from '@/components/Snackbar';
import { HelpLink } from '@/components/HelpLink';

const MIN_REASON = 10;
const MAX_REPORT_ROWS = 50;

export function BatchRestoreDialog({ ctx }: { ctx: BatchActionContext }) {
  const snackbar = useSnackbar();
  const [reason, setReason] = useState('');
  const [dryRunTaskId, setDryRunTaskId] = useState<string | null>(null);
  const [dryResult, setDryResult] = useState<BatchRestoreTaskResult | null>(null);

  // A changed selection invalidates the reviewed dry run (UX mirror only — the
  // backend's stale checks protect the apply regardless).
  const opIdentity = useMemo(
    () => JSON.stringify({ selection: ctx.selection, filterQuery: ctx.filterQuery }),
    [ctx.selection, ctx.filterQuery],
  );
  useEffect(() => {
    setDryRunTaskId(null);
    setDryResult(null);
  }, [opIdentity]);

  const taskQuery = useQuery({
    queryKey: ['batch-restore-task', dryRunTaskId],
    queryFn: () => bgtasksApi.retrieve(dryRunTaskId as string),
    enabled: dryRunTaskId != null && dryResult == null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state == null || isActiveState(state) ? 2000 : false;
    },
  });
  const taskState = taskQuery.data?.state ?? null;
  const taskFinished = taskState != null && !isActiveState(taskState);
  const taskSucceeded = taskState === TASK_STATE.COMPLETED;

  // The result archive lands moments after completion — retry through the gap.
  const resultQuery = useQuery({
    queryKey: ['batch-restore-result', dryRunTaskId],
    queryFn: () => bgtasksApi.result(dryRunTaskId as string),
    enabled: dryRunTaskId != null && taskSucceeded && dryResult == null,
    retry: (failureCount, error) =>
      failureCount < 10 && error instanceof ConsoleApiError && error.status === 404,
    retryDelay: 1200,
  });
  useEffect(() => {
    if (resultQuery.data) {
      setDryResult(resultQuery.data.result as unknown as BatchRestoreTaskResult);
    }
  }, [resultQuery.data]);

  const errorText = (e: unknown) =>
    e instanceof ConsoleApiError
      ? ((e.body as { detail?: string } | undefined)?.detail ?? e.message)
      : (e as Error).message;

  const dryRunMut = useMutation({
    mutationFn: () =>
      runBatch(
        'changes',
        'restore',
        ctx.selection,
        { reason: reason.trim(), dry_run: true },
        ctx.filterQuery,
      ),
    onSuccess: (r) => {
      setDryRunTaskId(r.task_id);
      if (r.capped) {
        snackbar.show(
          `Selection capped at ${r.processed.toLocaleString()} of ${r.matched.toLocaleString()} rows.`,
          'warning',
        );
      }
    },
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  const applyMut = useMutation({
    mutationFn: () =>
      runBatch(
        'changes',
        'restore',
        ctx.selection,
        { reason: reason.trim(), dry_run: false },
        ctx.filterQuery,
      ),
    onSuccess: (r) => {
      snackbar.show(
        `Restoring ${r.processed.toLocaleString()} change(s) in the background — follow it in Background Tasks.`,
        'success',
      );
      ctx.clearSelection();
      ctx.close();
    },
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  const busy = dryRunMut.isPending || applyMut.isPending;
  const waitingForDryRun = dryRunTaskId != null && dryResult == null && !taskFinished;
  const waitingForResult = taskSucceeded && dryResult == null;
  const reasonOk = reason.trim().length >= MIN_REASON;
  const wouldRestore = dryResult?.counts.would_restore ?? 0;
  const canApply = dryResult != null && reasonOk && !busy;

  return (
    <Dialog open onClose={busy ? undefined : ctx.close} fullWidth maxWidth="md">
      <DialogTitle>
        Restore {ctx.count.toLocaleString()} change(s) <HelpLink story="batch-edit" />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Each selected history row is written back to its <em>previous</em> values (the
            restorable fields only). Chained changes of one record unwind newest-first. A record
            that was edited again since its change was recorded is skipped, never overwritten —
            and rows already restored report as such, so re-running is safe.
          </Typography>

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">Acting on {ctx.matchedPhrase} change rows.</Alert>
          )}

          <TextField
            label="Why are you restoring these changes?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            required
            error={reason.length > 0 && !reasonOk}
            helperText={
              reason.length > 0 && !reasonOk
                ? `At least ${MIN_REASON} characters.`
                : 'Recorded on every restored record’s change history.'
            }
          />

          {waitingForDryRun && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip size="small" color="info" label="Dry run in progress" />
                <Typography variant="caption" color="text.secondary">
                  Walking every selected change without writing…
                </Typography>
              </Stack>
              <LinearProgress
                variant={taskQuery.data?.progress?.pct != null ? 'determinate' : 'indeterminate'}
                value={taskQuery.data?.progress?.pct ?? 0}
              />
            </Box>
          )}
          {waitingForResult && <LinearProgress />}
          {taskFinished && !taskSucceeded && (
            <Alert severity="error">
              The dry run did not complete — check it in Background Tasks, then run it again.
            </Alert>
          )}

          {dryResult && (
            <Box>
              <Alert severity={wouldRestore > 0 ? 'success' : 'warning'} sx={{ mb: 1 }}>
                <AlertTitle>
                  {wouldRestore > 0
                    ? `Dry run reviewed — ${wouldRestore.toLocaleString()} row(s) would be restored`
                    : 'Dry run complete — nothing to restore'}
                </AlertTitle>
                Records edited again in the meantime will be skipped at apply time.
              </Alert>

              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                {(Object.entries(dryResult.counts) as [BatchRestoreOutcome, number][]).map(
                  ([outcome, n]) => (
                    <Chip
                      key={outcome}
                      size="small"
                      color={RESTORE_OUTCOME_SEVERITY[outcome] ?? 'default'}
                      variant="outlined"
                      label={`${RESTORE_OUTCOME_LABELS[outcome] ?? outcome}: ${n.toLocaleString()}`}
                    />
                  ),
                )}
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Record</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Fields / detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dryResult.results.slice(0, MAX_REPORT_ROWS).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {row.object ? (
                          <>
                            {row.object.repr || `${row.object.content_type} #${row.object.object_pk}`}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (change #{row.id})
                            </Typography>
                          </>
                        ) : (
                          `change #${row.id}`
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={RESTORE_OUTCOME_SEVERITY[row.outcome] ?? 'default'}
                          variant="outlined"
                          label={RESTORE_OUTCOME_LABELS[row.outcome] ?? row.outcome}
                        />
                      </TableCell>
                      <TableCell>
                        {row.error ?? (row.fields ? row.fields.join(', ') : '—')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(dryResult.results.length > MAX_REPORT_ROWS || dryResult.results_truncated) && (
                <Typography variant="caption" color="text.secondary">
                  Showing the first {MAX_REPORT_ROWS} rows —{' '}
                  <RouterLink to={`/bgtasks/${dryRunTaskId}`}>full report</RouterLink>.
                </Typography>
              )}
            </Box>
          )}
          {!dryResult && !waitingForDryRun && !waitingForResult && (
            <Alert severity="info">
              <AlertTitle>Run a dry run first</AlertTitle>
              The dry run walks every selected change and reports exactly which records would be
              restored and which would be skipped (edited since, gone, or not restorable).
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={() => dryRunMut.mutate()}
          disabled={busy || !reasonOk || waitingForDryRun || waitingForResult}
        >
          Run dry run
        </Button>
        <Button variant="contained" color="warning" onClick={() => applyMut.mutate()} disabled={!canApply}>
          Restore{wouldRestore > 0 ? ` ${wouldRestore.toLocaleString()} row(s)` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
